import { Auth, Hub } from "aws-amplify";
import { HubCapsule } from "@aws-amplify/core";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { CognitoHelper } from "src/helpers/cognitoHelper";
import { ValidationError } from "src/errors";

Auth.configure({ authenticationFlowType: "CUSTOM_AUTH" });

class AuthenticationService {
  public async handleSignOut(): Promise<void> {
    try {
      await Auth.signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }

  public async getCurrentSession(): Promise<CognitoUserSession | undefined> {
    try {
      return await Auth.currentSession();
    } catch (error) {
      console.warn("Invalid current session:", error);
    }
  }

  public async getCurrentIdToken(): Promise<string | undefined> {
    const session = await this.getCurrentSession();
    return session?.getIdToken().getJwtToken();
  }

  public async signIn(email: string, password: string) {
    try {
      return await Auth.signIn(email, password);
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async sendCustomChallenge(user: any, otp: string) {
    try {
      return await Auth.sendCustomChallengeAnswer(user, otp);
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async signUp(email: string, password: string, confirmPassword: string) {
    if (password !== confirmPassword) {
      throw new ValidationError("Passwords do not match", "confirmPassword");
    }

    try {
      return await Auth.signUp({ username: email, password });
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async confirmSignUp(email: string, otp: string) {
    try {
      return await Auth.confirmSignUp(email, otp);
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async reSendSignUpCode(email: string) {
    try {
      return await Auth.resendSignUp(email);
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async forgotPassword(email: string) {
    try {
      return await Auth.forgotPassword(email);
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async forgotPasswordSubmit(email: string, otp: string, password: string, confirmPassword: string) {
    if (password !== confirmPassword) {
      throw new ValidationError("Passwords do not match", "confirmPassword");
    }

    try {
      return await Auth.forgotPasswordSubmit(email, otp, password);
    } catch (error) {
      throw CognitoHelper.resolveError(error);
    }
  }

  public async subscribeToAuthEvents(onAuthEvent: (capsule: HubCapsule) => void) {
    Hub.listen("auth", onAuthEvent);
  }
}

export { AuthenticationService };
